import type { SchemaNode } from '../generated/reference';

/**
 * Type of a node in TypeScript-like notation, the way storefront developers read it:
 * `string (uuid)`, `Money`, `CartLine[]`, `Record<string, Money>`, `Cart | Checkout`.
 * A named schema (`ref`) prints its name; `nullable` is a separate badge.
 */
export function schemaTypeLabel(node: SchemaNode): string {
  if (node.ref && (node.kind === 'object' || node.kind === 'oneOf' || node.circular)) {
    return node.ref;
  }
  switch (node.kind) {
    case 'array': {
      if (!node.items) return 'array';
      const { items } = node;
      if (items.kind === 'primitive' && items.format && !items.ref) {
        return `${items.type}[] (${items.format})`;
      }
      return `${wrap(schemaTypeLabel(items))}[]`;
    }
    case 'oneOf':
      return node.variants?.length ? node.variants.map(schemaTypeLabel).join(' | ') : 'oneOf';
    case 'object':
      return node.additionalProperties && !node.properties?.length
        ? `Record<string, ${schemaTypeLabel(node.additionalProperties)}>`
        : 'object';
    case 'primitive':
    case 'enum':
      return node.format ? `${node.type} (${node.format})` : node.type;
  }
}

function wrap(label: string): string {
  return label.includes(' | ') ? `(${label})` : label;
}

/**
 * The object whose properties a row expands into: the node itself, an array's items, a map's
 * values (unwrapping nested arrays). Null when there is nothing to expand.
 */
export function expandable(node: SchemaNode): SchemaNode | null {
  if (node.circular) return null;
  if (node.kind === 'array') return node.items ? expandable(node.items) : null;
  if (node.kind === 'oneOf') return node.variants?.length ? node : null;
  if (node.kind !== 'object') return null;
  if (node.properties?.length) return node;
  return node.additionalProperties ? expandable(node.additionalProperties) : null;
}

/** The circular node a row stands for, through arrays: `Category[]` of a cut `Category`. */
export function circularOf(node: SchemaNode): SchemaNode | null {
  if (node.circular) return node;
  return node.kind === 'array' && node.items ? circularOf(node.items) : null;
}

/** Example or default as it would appear in JSON. */
export function formatValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}
